/** @jsx jsx */
import { ReactNode, Fragment } from 'react';
import { css, jsx } from '@emotion/react';
import { Link } from 'gatsby';
import { useLocation } from '@reach/router';
import noop from '@atlaskit/ds-lib/noop';
import {
  AtlassianNavigation,
  PrimaryButton,
  ProductHome,
  useOverflowStatus,
} from '@atlaskit/atlassian-navigation';
import { LinkItem } from '@atlaskit/menu';
import { AtlassianIcon } from '@atlaskit/logo';
import { Logo, hideOnMobileNavigationStyles } from '@af/design-system-docs-ui';

import { TOP_NAV_ITEMS } from '../utils/constants';

import Settings from './settings';
import SearchButton from './search/search-button';
import MobileHeader from './mobile-header';
import type { SideNavigationItem } from './side-navigation';

const productHomePadding = 4;
const logoSvgWidth = 269;
const logoMaxWidth = productHomePadding * 2 + logoSvgWidth;

interface NavItemProps {
  href: string;
  pathname?: string;
  children: ReactNode;
}

const NavItem = ({ href, pathname = '', children }: NavItemProps) => {
  const { isVisible } = useOverflowStatus();
  return (
    <div css={hideOnMobileNavigationStyles}>
      {isVisible ? (
        <PrimaryButton
          isHighlighted={pathname.startsWith(href)}
          component={Link}
          // @ts-expect-error
          to={href}
        >
          {children}
        </PrimaryButton>
      ) : (
        <LinkItem href={href} isSelected={pathname.startsWith(href)}>
          {children}
        </LinkItem>
      )}
    </div>
  );
};

const linkStyles = css({
  display: 'flex',
});

interface TopNavProps {
  navigation: SideNavigationItem[];
  onSearchOpen(): void;
}

const TopNav = ({ navigation, onSearchOpen }: TopNavProps) => {
  const { pathname } = useLocation();

  return (
    <Fragment>
      <div css={hideOnMobileNavigationStyles}>
        <AtlassianNavigation
          label="site navigation"
          renderProductHome={() => (
            <ProductHome
              // Logos are wrapped in Links rather than using
              // the `href` prop so we can utilize the router to
              // prevent page reloads.
              icon={() => (
                <Link to="/" css={linkStyles}>
                  <AtlassianIcon
                    label="The Atlassian icon"
                    appearance="brand"
                    size="small"
                  />
                </Link>
              )}
              logo={() => (
                <Link to="/" css={linkStyles}>
                  <Logo id="desktop-header-logo" />
                </Link>
              )}
              // FIXME: Hack to get ProductHome to allow click through
              // to the links. Otherwise `pointer-events: none` is
              // applied :(
              onClick={noop}
              logoMaxWidth={logoMaxWidth}
            />
          )}
          renderSearch={() => <SearchButton onClick={onSearchOpen} />}
          renderSettings={() => <Settings />}
          primaryItems={TOP_NAV_ITEMS.map(({ title, slug }) => (
            <NavItem pathname={pathname} href={`/${slug}`}>
              {title}
            </NavItem>
          ))}
        />
      </div>
      <MobileHeader navigation={navigation} onSearchOpen={onSearchOpen} />
    </Fragment>
  );
};

export default TopNav;
